import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowSquareOutIcon as Out, XIcon as X, WarningIcon as Warning } from "@phosphor-icons/react";
import BindingStatusBadge from "@/components/screenbindings/BindingStatusBadge";
import OriginBadge from "@/components/screenbindings/OriginBadge";
import StepFieldTable from "@/components/screenwalkthrough/StepFieldTable";
import { operationLabel, statusMeta } from "@/lib/screenBindings";
import { splitFields, stepTarget } from "@/lib/screenWalkthrough";

function Row({ label, value, mono, testid }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1 border-b border-[var(--ml-border)] last:border-b-0 text-xs">
      <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] shrink-0">{label}</span>
      <span className={`text-right break-words ${mono ? "ml-mono" : ""}`} data-testid={testid}>{value ?? <span className="text-[var(--ml-text-secondary)]">—</span>}</span>
    </div>
  );
}

/** Iter 47 — detail panel for one observed screen step: RPG proof, ambiguity, fields split by role, DB mapping. */
export default function StepDetail({ workspaceId, step, onClose }) {
  useEffect(() => {
    if (!step) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step, onClose]);

  if (!step) return null;
  const m = statusMeta(step.status);
  const { input, output } = splitFields(step.fields);
  const t = step.field_totals || {};
  return (
    <section className="ml-card p-4 space-y-4" data-testid="sw-step-detail" data-sequence={step.sequence_number}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Step {step.sequence_number} · observed screen statement</div>
          <div className="text-lg font-semibold ml-mono mt-0.5" data-testid="sw-detail-title">{step.program_name} {operationLabel(step.operation)} {stepTarget(step)}</div>
        </div>
        <button type="button" onClick={onClose} className="p-1 hover:bg-[var(--ml-surface)]" data-testid="sw-detail-close" aria-label="Close"><X size={16} /></button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <BindingStatusBadge status={step.status} confidence={step.confidence} testid="sw-detail-status" />
        {step.manual && <OriginBadge origin="manual" testid="sw-detail-origin" />}
      </div>
      {step.manual && (
        <div className="text-xs px-3 py-2 bg-[#fff1f1] text-[#a2191f] space-y-1" data-testid="sw-detail-manual">
          <div className="flex items-start gap-2">
            <Warning size={14} weight="bold" className="shrink-0 mt-0.5" />
            <span><strong>{step.manual_label || "Manual binding — consultant supplied, not parser-proven"}.</strong>{" "}
              {step.source_line != null
                ? `The ${operationLabel(step.operation)} ${step.record_format} statement at line ${step.source_line} is observed in source; the display file ${step.display_file} was supplied by a consultant.`
                : "No RPG statement was observed for this screen; its position in the walkthrough is not observed in source."}
            </span>
          </div>
          {step.manual_binding && (
            <div className="pl-6 space-y-0.5" data-testid="sw-detail-manual-note">
              <div><span className="ml-mono text-[10px] uppercase tracking-widest">Consultant note</span> — {step.manual_binding.manual_note || "—"}</div>
              <div className="text-[11px]">
                Manual binding #{step.manual_binding.id} · consultant confidence {step.manual_binding.confidence} · created by {step.manual_binding.created_by || "—"}
                {step.manual_binding.updated_by && step.manual_binding.updated_by !== step.manual_binding.created_by ? ` · updated by ${step.manual_binding.updated_by}` : ""}
                {step.manual_binding.active === false ? " · deactivated" : ""}
              </div>
            </div>
          )}
        </div>
      )}
      {step.ambiguity_reason && !step.manual && (
        <p className="text-xs px-3 py-2 flex items-start gap-2" style={{ background: m.bg, color: m.fg }} data-testid="sw-detail-ambiguity">
          <Warning size={14} weight="bold" className="shrink-0 mt-0.5" /> <span>{step.ambiguity_reason}</span>
        </p>
      )}
      {step.candidate_display_files?.length > 0 && (
        <p className="text-xs text-[var(--ml-text-secondary)]" data-testid="sw-detail-candidates">
          Parsed display files that define <span className="ml-mono">{step.record_format}</span>: {step.candidate_display_files.join(", ")} — none is claimed; fields are not listed for an ambiguous format name.
        </p>
      )}
      <div>
        <Row label="Program" value={`${step.program_library ? `${step.program_library}/` : ""}${step.program_name}`} mono testid="sw-detail-program" />
        <Row label="Source" value={step.source_file ? `${step.source_file}/${step.source_member || "?"}` : step.source_member} mono />
        <Row label="Display file" value={step.display_file || m.wording} mono testid="sw-detail-display-file" />
        <Row label="Record format" value={step.record_format} mono testid="sw-detail-record-format" />
        <Row label="Operation" value={`${operationLabel(step.operation)} — program statement that ${step.operation === "exfmt" || step.operation === "write" ? "displays" : "reads"} the format`} testid="sw-detail-operation" />
        <Row label="RPG source line" value={step.source_line ?? (step.manual ? "none — consultant supplied, no source statement claimed" : null)} mono={step.source_line != null} testid="sw-detail-line" />
        <Row label="Confidence" value={step.manual ? `${step.confidence} (consultant)` : step.confidence} mono testid="sw-detail-confidence" />
      </div>
      <div>
        <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1">
          {step.source_excerpt ? "RPG statement that proves the screen use" : "RPG statement"}
        </div>
        {step.source_excerpt ? (
          <pre className="text-[11px] ml-mono bg-[var(--ml-surface)] border border-[var(--ml-border)] p-2 overflow-x-auto whitespace-pre" data-testid="sw-detail-excerpt">{step.source_excerpt}</pre>
        ) : (
          <p className="text-[11px] ml-mono bg-[var(--ml-surface)] border border-[var(--ml-border)] p-2 text-[var(--ml-text-secondary)]" data-testid="sw-detail-excerpt">
            {step.manual ? "no source excerpt — manual binding, not parser-proven" : "—"}
          </p>
        )}
      </div>
      {step.fields_note && (
        <p className="text-xs px-3 py-2 bg-[#fff8e1] text-[#8a6d00]" data-testid="sw-detail-fields-note">{step.fields_note}</p>
      )}
      {step.status === "bound" ? (
        <>
          <div className="text-xs text-[var(--ml-text-secondary)]" data-testid="sw-detail-field-totals">
            {t.fields || 0} screen field{(t.fields || 0) === 1 ? "" : "s"} in {step.display_file}/{step.record_format} — <strong style={{ color: "#0043ce" }}>{t.input || 0} input-capable</strong>, {(t.output || 0) + (t.display || 0)} output/display · {t.proven_mappings || 0} proven DB mapping{(t.proven_mappings || 0) === 1 ? "" : "s"} · <span style={{ color: "#8a6d00" }}>{t.ambiguous_mappings || 0} ambiguous</span> · {t.screen_only || 0} screen-only
          </div>
          <StepFieldTable title="Input-capable fields (users can type here)" fields={input} workspaceId={workspaceId} displayFile={step.display_file} highlight testid="sw-input-fields" emptyText="No input-capable field in this format — display-only screen." />
          <StepFieldTable title="Output / display fields (shown back to the user, hidden or control)" fields={output} workspaceId={workspaceId} displayFile={step.display_file} testid="sw-output-fields" emptyText="No output/display field in this format." />
        </>
      ) : (
        <p className="text-xs text-[var(--ml-text-secondary)]" data-testid="sw-detail-no-fields">
          No display file is bound to this statement, so no fields are attached — nothing is invented.
        </p>
      )}
      <div className="flex flex-wrap gap-3 text-xs">
        {step.screen_format_binding_id && (
          <Link to={`/workspaces/${workspaceId}/metadata/screen-bindings?binding=${step.screen_format_binding_id}`} className="text-[var(--ml-primary)] hover:underline inline-flex items-center gap-1" data-testid="sw-detail-binding-link">
            Screen format binding #{step.screen_format_binding_id} <Out size={11} />
          </Link>
        )}
        {step.manual_binding_id && step.manual_binding_id !== step.screen_format_binding_id && (
          <Link to={`/workspaces/${workspaceId}/metadata/screen-bindings?binding=${step.manual_binding_id}`} className="text-[#a2191f] hover:underline inline-flex items-center gap-1" data-testid="sw-detail-manual-binding-link">
            Manual binding #{step.manual_binding_id} <Out size={11} />
          </Link>
        )}
        <Link to={`/workspaces/${workspaceId}/metadata/source-parser?program=${encodeURIComponent(step.program_name)}`} className="text-[var(--ml-primary)] hover:underline inline-flex items-center gap-1" data-testid="sw-detail-rpg-link">
          Parsed RPG facts <Out size={11} />
        </Link>
        {step.display_file && (
          <Link to={`/workspaces/${workspaceId}/metadata/source-parser?program=${encodeURIComponent(step.display_file)}`} className="text-[var(--ml-primary)] hover:underline inline-flex items-center gap-1" data-testid="sw-detail-dspf-link">
            Parsed DDS facts <Out size={11} />
          </Link>
        )}
      </div>
    </section>
  );
}
